import React, { useState, useEffect, useContext } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Chip,
  Grid,
  Alert,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import { AuthContext } from '../../context/AuthContext';
import axios from '../../api/client';
import EmptyState from '../EmptyState';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import LandlordPayoutsDue from './LandlordPayoutsDue';
import ScrollTable from '../ScrollTable';


// Mirrors backend/utils/accountCategories.js.
const ACCOUNT_CATEGORY_LABELS = { cash: 'Cash', bank: 'Bank', mobile_money: 'Mobile Money', other: 'Other' };

const EMPTY_FORM = {
  landlordId: '',
  propertyId: '',
  amount: '',
  paymentDate: new Date().toISOString().split('T')[0],
  paymentModeId: '',
  paidFromAccountId: '',
  reference: '',
  notes: '',
  periodStart: '',
  periodEnd: '',
  payoutRunId: '',
};

const LandlordPayments = () => {
  const { can } = useContext(AuthContext);
  const [payments, setPayments] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [assetAccounts, setAssetAccounts] = useState([]);
  const { formatMoney: formatCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [balance, setBalance] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState(0);
  const [properties, setProperties] = useState([]);
  const [filters, setFilters] = useState({ landlord: '', property: '', status: '', search: '' });
  // Kept separate from the form's `properties`. Both list properties for a landlord, but one
  // drives the filter and the other the record-payment dialog — sharing them meant opening
  // the dialog silently changed which properties the filter offered.
  const [filterProperties, setFilterProperties] = useState([]);
  const [payableRuns, setPayableRuns] = useState([]);

  useEffect(() => {
    fetchPayments();
    fetchLandlords();
    fetchPaymentModes();
    fetchAssetAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Searched on the SERVER, because this list pages. Debounced so a request per keystroke
  // cannot race and land an older, wider answer on top of a newer one.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchPayments(0, rowsPerPage, filters);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const fetchPayments = async (targetPage = page, targetSize = rowsPerPage, activeFilters = filters) => {
    try {
      const res = await axios.get('/landlord-payments', {
        params: {
          page: targetPage + 1,
          limit: targetSize,
          ...(activeFilters.landlord ? { landlord: activeFilters.landlord } : {}),
          ...(activeFilters.property ? { property: activeFilters.property } : {}),
          ...(activeFilters.status ? { status: activeFilters.status } : {}),
          ...(activeFilters.search ? { search: activeFilters.search } : {})
        },
      });
      const body = res.data;
      setPayments(Array.isArray(body) ? body : (body.data || []));
      setTotal(Array.isArray(body) ? body.length : (body.total ?? 0));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching landlord payments');
    }
  };

  const fetchLandlords = async () => {
    try {
      const res = await axios.get('/landlords');
      setLandlords(Array.isArray(res.data) ? res.data : (res.data.data || []));
    } catch (error) {
      setLandlords([]);
    }
  };

  const fetchPaymentModes = async () => {
    try {
      const res = await axios.get('/payment-modes?isActive=true');
      setPaymentModes(res.data || []);
    } catch (error) {
      setPaymentModes([]);
    }
  };

  const fetchAssetAccounts = async () => {
    try {
      const res = await axios.get('/accounting/chart-of-accounts');
      const all = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setAssetAccounts(all.filter((a) => a.type === 'asset' && a.isActive !== false));
    } catch (error) {
      setAssetAccounts([]);
    }
  };

  // What the landlord is currently owed, so whoever is paying can see it rather than
  // working it out from a separate statement screen.
  const fetchLandlordProperties = async (landlordId) => {
    if (!landlordId) { setProperties([]); return; }
    try {
      const res = await axios.get(`/landlord-statements/${landlordId}/properties`);
      setProperties(res.data.properties || []);
    } catch (e) {
      setProperties([]);
    }
  };

  // Changing a filter always returns to the first page. Staying on page 4 of a narrower
  // result set shows an empty table, which reads as "no payments" rather than "no page 4".
  const applyFilter = async (changes) => {
    const next = { ...filters, ...changes };
    setFilters(next);
    setPage(0);
    fetchPayments(0, rowsPerPage, next);
  };

  const onFilterLandlord = async (landlordId) => {
    // The property filter is scoped to the chosen landlord, so a property left selected from
    // a previous landlord would filter to a building they do not own and return nothing.
    applyFilter({ landlord: landlordId, property: '' });
    if (!landlordId) { setFilterProperties([]); return; }
    try {
      const res = await axios.get(`/landlord-statements/${landlordId}/properties`);
      setFilterProperties(res.data.properties || []);
    } catch (e) {
      setFilterProperties([]);
    }
  };

  // Finalised evaluations nobody has paid yet. Picking one fills the form from the sheet,
  // so the figure paid is the figure the landlord was shown.
  const fetchPayableRuns = async (landlordId) => {
    if (!landlordId) { setPayableRuns([]); return; }
    try {
      const res = await axios.get('/landlord-payout-runs', { params: { landlordId, unpaid: 1 } });
      setPayableRuns(res.data || []);
    } catch (e) {
      setPayableRuns([]);
    }
  };

  const pickRun = (runId) => {
    const run = payableRuns.find((r) => r._id === runId);
    if (!run) { setForm({ ...form, payoutRunId: '' }); return; }
    setForm({
      ...form,
      payoutRunId: run._id,
      propertyId: run.propertyId || '',
      amount: String(Number(run.amountPayable) || ''),
      periodStart: run.periodStart || '',
      periodEnd: run.periodEnd || ''
    });
  };

  const fetchBalance = async (landlordId) => {
    if (!landlordId) { setBalance(null); return; }
    try {
      const res = await axios.get(`/landlord-statements/${landlordId}/balance`);
      setBalance(res.data);
    } catch (error) {
      setBalance(null);
    }
  };

  const selectedMode = paymentModes.find((m) => m._id === form.paymentModeId) || null;
  const accountsForSelectedMode = selectedMode
    ? assetAccounts.filter((a) => a.accountCategory === selectedMode.accountCategory)
    : [];
  const selectedModeCategoryLabel = selectedMode
    ? (ACCOUNT_CATEGORY_LABELS[selectedMode.accountCategory] || selectedMode.accountCategory)
    : '';

  const handleOpen = () => {
    setForm(EMPTY_FORM);
    setBalance(null);
    setProperties([]);
    setPayableRuns([]);
    setOpen(true);
  };

  // Opened from a row on the Due tab: the landlord, property and expected amount are all
  // already known, so the accountant is not asked to re-enter what the schedule just told
  // them — which is also where transcription mistakes come from.
  const handlePayFromSchedule = (row) => {
    setForm({
      ...EMPTY_FORM,
      landlordId: row.landlordId,
      propertyId: row.propertyId,
      amount: String(row.expectedNet),
      periodStart: new Date(row.dueDate.slice ? row.dueDate.slice(0, 10) : row.dueDate)
        .toISOString().slice(0, 8) + '01',
      periodEnd: new Date(row.dueDate).toISOString().slice(0, 10)
    });
    setBalance(null);
    fetchPayableRuns(row.landlordId);
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.landlordId) { toast.error('Please select a landlord'); return; }
    const amount = parseFloat(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }
    if (!form.paidFromAccountId) {
      toast.error('Please record which account this was paid from');
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post('/landlord-payments', { ...form, amount });
      toast.success('Landlord payment recorded and posted');
      // Paying more than is owed is allowed, but say so rather than letting the balance
      // quietly go negative.
      if (res.data?.warning) toast.warn(res.data.warning);
      setOpen(false);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error recording payment');
    } finally {
      setSaving(false);
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) {
      toast.error('Please give a reason for voiding this payment');
      return;
    }
    try {
      await axios.post(`/landlord-payments/${voidTarget._id}/void`, { reason: voidReason.trim() });
      toast.success('Payment voided — a reversing entry has been posted');
      setVoidTarget(null);
      setVoidReason('');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error voiding payment');
    }
  };

  const handleChangePage = (_e, newPage) => { setPage(newPage); fetchPayments(newPage, rowsPerPage); };
  const handleChangeRowsPerPage = (e) => {
    const size = parseInt(e.target.value, 10);
    setRowsPerPage(size); setPage(0); fetchPayments(0, size);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h5">Landlord Payments</Typography>
        {can('landlordPayments', 'create') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
            Record Payment
          </Button>
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Due for Payment" />
        <Tab label="Recorded Payments" />
      </Tabs>

      {tab === 0 && <LandlordPayoutsDue onPay={handlePayFromSchedule} />}

      {tab === 1 && (
        <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Money paid out to landlords — their share of collected rent after management fees and
        any expenses recharged to them. Each payment posts to the ledger immediately and
        reduces what the landlord is owed. Mistakes are voided, not deleted.
      </Alert>

      <Box mb={2} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          size="small" label="Search" placeholder="Payment number, landlord or note"
          sx={{ minWidth: 240 }}
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
        />
        <TextField
          select size="small" label="Landlord" sx={{ minWidth: 200 }}
          value={filters.landlord}
          onChange={(e) => onFilterLandlord(e.target.value)}
        >
          <MenuItem value="">All landlords</MenuItem>
          {landlords.map((l) => (
            <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          select size="small" label="Property" sx={{ minWidth: 220 }}
          value={filters.property}
          onChange={(e) => applyFilter({ property: e.target.value })}
          disabled={!filters.landlord}
          helperText={filters.landlord
            ? 'Includes lump payments covering all their properties'
            : 'Choose a landlord first'}
        >
          <MenuItem value="">All properties</MenuItem>
          {filterProperties.map((p) => (
            <MenuItem key={p._id || p.propertyId} value={p._id || p.propertyId}>
              {p.propertyName}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select size="small" label="Status" sx={{ minWidth: 140 }}
          value={filters.status}
          onChange={(e) => applyFilter({ status: e.target.value })}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="posted">Posted</MenuItem>
          <MenuItem value="voided">Voided</MenuItem>
        </TextField>

        {(filters.landlord || filters.property || filters.status) && (
          <Button
            size="small" sx={{ mt: 0.5 }}
            onClick={() => { setFilterProperties([]); applyFilter({ landlord: '', property: '', status: '' }); }}
          >
            Clear
          </Button>
        )}
      </Box>

      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Number</TableCell>
              <TableCell>Landlord</TableCell>
              <TableCell>Property</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Paid From</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Recorded By</TableCell>
              <TableCell>Status</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p._id} sx={p.status === 'voided' ? { opacity: 0.6 } : undefined}>
                <TableCell className="pin-left" sx={{ fontFamily: 'monospace' }}>{p.paymentNumber}</TableCell>
                <TableCell>{p.landlord?.name || '-'}</TableCell>
                <TableCell>
                  {/* A payout with no property is a lump covering all of the landlord's
                      buildings. Saying so beats a dash, which reads as missing data. */}
                  {p.property?.propertyName || (
                    <Typography variant="caption" color="text.secondary">All properties</Typography>
                  )}
                </TableCell>
                <TableCell>{formatCurrency(p.amount)}</TableCell>
                <TableCell>{p.paymentDate ? format(new Date(p.paymentDate), 'dd MMM yyyy') : '-'}</TableCell>
                <TableCell>
                  {p.paidFromAccount ? `${p.paidFromAccount.code} ${p.paidFromAccount.name}` : '-'}
                  {p.paymentMode ? ` · ${p.paymentMode.name}` : ''}
                </TableCell>
                <TableCell>
                  {p.reference || '-'}
                  {/* The evaluation it settled, so a payment can be traced to the sheet the
                      landlord was handed. */}
                  {p.payoutRun?.runNumber && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {p.payoutRun.runNumber}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{p.createdBy?.name || '-'}</TableCell>
                <TableCell>
                  {p.status === 'voided' ? (
                    <Tooltip title={`${p.voidReason || ''} — ${p.voidedBy?.name || ''}`}>
                      <Chip label="Voided" color="error" size="small" variant="outlined" />
                    </Tooltip>
                  ) : (
                    <Chip label="Posted" color="success" size="small" />
                  )}
                </TableCell>
                <TableCell className="pin-right">
                  {p.status === 'posted' && can('landlordPayments', 'delete') && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<BlockIcon />}
                      onClick={() => { setVoidTarget(p); setVoidReason(''); }}
                    >
                      Void
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <EmptyState
                    title="No landlord payments yet"
                    description="Payments made to landlords appear here, with the ledger entry each one posted."
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
      />
        </>
      )}

      {/* Record payment */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Landlord Payment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            This posts to the ledger as soon as it is saved. The amount, landlord, date and
            account cannot be changed afterwards — a mistake is voided and re-entered.
          </Alert>

          <TextField
            fullWidth
            select
            required
            margin="normal"
            label="Landlord"
            value={form.landlordId}
            onChange={(e) => {
              setForm({ ...form, landlordId: e.target.value, propertyId: '', payoutRunId: '' });
              fetchBalance(e.target.value);
              fetchLandlordProperties(e.target.value);
              fetchPayableRuns(e.target.value);
            }}
          >
            {landlords.map((l) => (
              <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>
            ))}
          </TextField>

          {/* The evaluation this payment settles, when there is one waiting. Optional: a
              payment can still be recorded on its own. Shown only when something is payable,
              so the form stays as it was for landlords with no runs. */}
          {payableRuns.length > 0 && (
            <TextField
              fullWidth
              select
              margin="normal"
              label="Payout run (optional)"
              value={form.payoutRunId}
              onChange={(e) => pickRun(e.target.value)}
              helperText="A finalised payment report waiting to be paid. Picking one fills in the amount, property and period."
            >
              <MenuItem value="">None — record the payment on its own</MenuItem>
              {payableRuns.map((r) => (
                <MenuItem key={r._id} value={r._id}>
                  {r.runNumber} · {r.property?.propertyName || 'All properties'} · {r.periodStart} to {r.periodEnd} · {formatCurrency(r.amountPayable)}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Optional. Each property has its own pay day, so attributing the payout lets the
              Due tab mark that row settled. Left blank when a landlord is paid one lump
              across several buildings — the schedule then treats it as covering them all. */}
          <TextField
            fullWidth
            select
            margin="normal"
            label="Property (optional)"
            value={form.propertyId}
            onChange={(e) => setForm({ ...form, propertyId: e.target.value })}
            helperText={form.landlordId
              ? 'Which property this payout covers. Leave blank if it covers all of them.'
              : 'Choose a landlord first'}
            disabled={!form.landlordId}
          >
            <MenuItem value="">All properties (one lump payment)</MenuItem>
            {properties.map((p) => (
              <MenuItem key={p._id || p.propertyId} value={p._id || p.propertyId}>
                {p.propertyName}
              </MenuItem>
            ))}
          </TextField>

          {balance && (
            <Alert severity={Number(balance.closingBalance) > 0 ? 'info' : 'success'} sx={{ mb: 1 }}>
              Currently payable: <strong>{formatCurrency(balance.closingBalance)}</strong>
              {balance.totalPaidOut > 0 && (
                <> · already paid {formatCurrency(balance.totalPaidOut)}</>
              )}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                margin="normal"
                type="number"
                label="Amount"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                margin="normal"
                type="date"
                label="Payment Date"
                InputLabelProps={{ shrink: true }}
                value={form.paymentDate}
                onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                required
                margin="normal"
                label="Paid By"
                value={form.paymentModeId}
                onChange={(e) => {
                  const mode = paymentModes.find((m) => m._id === e.target.value);
                  const matching = assetAccounts.filter((a) => a.accountCategory === mode?.accountCategory);
                  setForm({
                    ...form,
                    paymentModeId: e.target.value,
                    paidFromAccountId: matching.length === 1 ? (matching[0]._id || matching[0].id) : '',
                  });
                }}
                helperText={paymentModes.length === 0 ? 'No modes set up' : 'How the money went out'}
              >
                {paymentModes.map((m) => (
                  <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                required
                margin="normal"
                label={selectedModeCategoryLabel ? `${selectedModeCategoryLabel} Account` : 'Paid From'}
                value={form.paidFromAccountId}
                onChange={(e) => setForm({ ...form, paidFromAccountId: e.target.value })}
                disabled={!form.paymentModeId}
                helperText={
                  !form.paymentModeId
                    ? 'Choose how it was paid first'
                    : accountsForSelectedMode.length === 0
                      ? `No ${selectedModeCategoryLabel} accounts set up`
                      : 'Which account the money came out of'
                }
              >
                {accountsForSelectedMode.map((a) => (
                  <MenuItem key={a._id || a.id} value={a._id || a.id}>
                    {a.code} — {a.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            margin="normal"
            label="Reference"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            helperText="Cheque number, transfer reference, transaction ID"
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                type="date"
                label="Period From"
                InputLabelProps={{ shrink: true }}
                value={form.periodStart}
                onChange={(e) => setForm({ ...form, periodStart: e.target.value })}
                helperText="Optional — what this settles"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                type="date"
                label="Period To"
                InputLabelProps={{ shrink: true }}
                value={form.periodEnd}
                onChange={(e) => setForm({ ...form, periodEnd: e.target.value })}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            multiline
            rows={2}
            margin="normal"
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !form.landlordId || !form.amount || !form.paidFromAccountId}
          >
            {saving ? 'Posting...' : 'Record & Post'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void */}
      <Dialog open={!!voidTarget} onClose={() => setVoidTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Void Landlord Payment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            Voiding posts a reversing entry. Nothing is deleted — both the original and the
            reversal stay in the ledger, and the amount goes back onto what the landlord is
            owed.
          </Alert>
          {voidTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {voidTarget.paymentNumber} · {voidTarget.landlord?.name} ·{' '}
              {formatCurrency(voidTarget.amount)}
            </Typography>
          )}
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Reason"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            helperText="Recorded against the payment and the reversing entry"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidTarget(null)}>Cancel</Button>
          <Button onClick={handleVoid} variant="contained" color="error" disabled={!voidReason.trim()}>
            Void Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LandlordPayments;
